import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography} from 'views/components';
import {HoverToolBox, translate} from 'views/module';

import Message from './Message';
import EmailMessage from './EmailMessage';

import displayResource from 'utils/miscellaneous/display_resource';
import parseSesEmail from 'utils/miscellaneous/email/parse_ses_email';
import moment from 'moment';

const MessageFeedBox = (props) => {
  const emptyEmail = {
    from: '',
    to: '',
    replyTo: '',
    subject: '',
    date: '',
    html: '',
    text: '',
  };
  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [mouseOver, setMouseOver] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [parsedEmail, setParsedEmail] = React.useState(emptyEmail);

  const {
    message,
    userRn,
    caregiverBackend,
  } = props;

  const isMessageTypeEmail = message.messageType === 'EMAIL';
  const emailBlob = message?.body?.blob;
  const showEmailMessage = isMessageTypeEmail && message?.body?.editorType !== 'MARKDOWN';
  const createdAtMoment = moment(message.createdAt);
  const isToday = createdAtMoment.isSame(moment(), 'day');

  React.useEffect(() => {
    let isActive = true;

    if (!showEmailMessage) {
      setParsedEmail(emptyEmail);
      return () => {
        isActive = false;
      };
    }

    setParsedEmail(emptyEmail);

    parseSesEmail(emailBlob).then((value) => {
      if (!isActive) {
        return;
      }

      setParsedEmail(value);
    });

    return () => {
      isActive = false;
    };
  }, [emailBlob, showEmailMessage]);

  React.useEffect(() => {
    if (editMode) {
      setToolbarVisible(false);
      return;
    }
    setToolbarVisible(mouseOver);
  }, [editMode, mouseOver]);

  const submittedBy = message.submittedBy;
  const author = displayResource(submittedBy);
  const smsAuthor = React.useMemo(() => {
    const messageBody = message?.body?.blob || '';
    const senderName = messageBody.match(/^([^:\n]+):/)?.[1]?.trim();

    return senderName || author;
  }, [author, message?.body?.blob]);
  const emailAuthor = React.useMemo(() => {
    const fromValue = parsedEmail.from || '';
    const fromName = fromValue.match(/^(.*?)\s*</)?.[1]?.trim();

    return fromName || fromValue.trim();
  }, [parsedEmail.from]);
  const messageHeader = React.useMemo(() => {
    switch (message.messageType) {
      case 'ANNOUNCEMENTS':
        return {
          title: translate('caregiver.CaregiverDetail.Correspondance.Feed.Titles.AnnouncementPosted'),
          subTitle: [author].filter(Boolean),
        };
      case 'COMMENTS':
        return {
          title: translate('caregiver.CaregiverDetail.Correspondance.Feed.Titles.CommentAdded'),
          subTitle: [author].filter(Boolean),
        };
      case 'DIALPAD_SMS_TEXT':
        return {
          title: translate('caregiver.CaregiverDetail.Correspondance.Feed.Titles.SmsMessageSent'),
          subTitle: [
            translate('caregiver.CaregiverDetail.Correspondance.Feed.Metadata.Sms'),
            smsAuthor,
          ].filter(Boolean),
        };
      case 'DIALPAD_CALL_TRANSCRIPT':
        return {
          title: translate('caregiver.CaregiverDetail.Correspondance.Feed.Titles.DialpadCall'),
          subTitle: [
            translate('caregiver.CaregiverDetail.Correspondance.Feed.Metadata.Call'),
            author,
          ].filter(Boolean),
        };
      case 'EMAIL':
        return {
          title: translate('caregiver.CaregiverDetail.Correspondance.Feed.Titles.EmailSent'),
          subTitle: [
            translate('caregiver.CaregiverDetail.Correspondance.Feed.Metadata.Email'),
            emailAuthor,
          ].filter(Boolean),
        };
      default:
        return {
          title: translate('caregiver.CaregiverDetail.Correspondance.Correspondance.CommentedOn'),
          subTitle: [author].filter(Boolean),
        };
    }
  }, [author, emailAuthor, message.messageType, smsAuthor]);
  const timelineLabel = React.useMemo(() => {
    if (!isToday) {
      return {
        primary: createdAtMoment.format('MMM D'),
        secondary: createdAtMoment.format('h:mm A'),
      };
    }

    const diffMinutes = Math.max(moment().diff(createdAtMoment, 'minutes'), 0);
    if (diffMinutes < 1) {
      return {
        primary: translate('caregiver.CaregiverDetail.Correspondance.Feed.RelativeTime.Today'),
        secondary: translate('caregiver.CaregiverDetail.Correspondance.Feed.RelativeTime.Now'),
      };
    }

    if (diffMinutes < 60) {
      return {
        primary: translate('caregiver.CaregiverDetail.Correspondance.Feed.RelativeTime.Today'),
        secondary: translate('caregiver.CaregiverDetail.Correspondance.Feed.RelativeTime.MinutesAgo', {
          count: diffMinutes,
        }),
      };
    }

    const diffHours = Math.max(moment().diff(createdAtMoment, 'hours'), 1);
    return {
      primary: translate('caregiver.CaregiverDetail.Correspondance.Feed.RelativeTime.Today'),
      secondary: translate('caregiver.CaregiverDetail.Correspondance.Feed.RelativeTime.HoursAgo', {
        count: diffHours,
      }),
    };
  }, [createdAtMoment, isToday]);

  const onClickEdit = () => {
    setEditMode(true);
  };

  const onClickEditSave = (event, newMessage, setLoading) => {
    caregiverBackend.messages.update(message.messageRn, newMessage,
        () => {
          setEditMode(false);
        }, () => {
          setEditMode(false);
        });
  };

  const onClickEditCancel = () => {
    setEditMode(false);
  };

  const onEmojiSelected = (emoji) => {
    const prevMessage = message;
    const prevEmoji = prevMessage.emoji;
    // find if we previously selected this emoji, then remove, else add
    if (prevEmoji) {
      const prevEmojiUsers = prevEmoji[emoji];
      if (prevEmojiUsers) {
        const requesterIndex = prevEmojiUsers.findIndex((user) => user === userRn);
        if (requesterIndex > -1) {
          caregiverBackend.messages.removeEmoji(message.messageRn, emoji, userRn);
          return;
        }
      }
    }
    caregiverBackend.messages.addEmoji(message.messageRn, emoji, userRn);
  };

  const onClickDelete = (setIsDeleting) => {
    setIsDeleting(true);
    caregiverBackend.messages.delete(
        message.messageRn,
        () => setIsDeleting(false),
        () => setIsDeleting(false),
    );
  };
  return (
    <Box
      onMouseOver={(event) => {
        setMouseOver(true);
        setAnchorEl(event.currentTarget);
      }}
      onMouseLeave={() => setMouseOver(false)}
      sx={{
        borderRadius: '10px',
        backgroundColor: toolbarVisible ? '#F9F8F6' : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
    >
      <Stack
        direction='row'
        spacing={{md: 2, xs: 1.25}}
        alignItems='flex-start'
        sx={{
          py: 0.5,
          px: 0.5,
        }}
      >
        <Stack
          spacing={0.1}
          sx={{
            width: '64px',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '14px',
              color: isToday ? '#D97706' : '#7C7C7C',
            }}
          >
            {timelineLabel.primary}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '14px',
              color: isToday ? '#D97706' : '#7C7C7C',
            }}
          >
            {timelineLabel.secondary}
          </Typography>
        </Stack>
        <Box
          sx={{
            pt: '4px',
            width: '10px',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: '#D8D3CC',
              border: '1.5px solid #D8D3CC',
            }}
          />
        </Box>
        <Stack
          spacing={0.75}
          sx={{
            minWidth: 0,
            flex: 1,
            pr: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              lineHeight: '17px',
              fontWeight: 500,
              color: '#1A1916',
            }}
          >
            {messageHeader.title}
          </Typography>
          {
            messageHeader.subTitle.length > 0 &&
            <Stack
              direction='row'
              spacing={0.75}
              sx={{
                flexWrap: 'wrap',
                rowGap: 0.25,
              }}
            >
              {
                messageHeader.subTitle.map((item, index) => {
                  return (
                    <Typography
                      key={`${item}-${index}`}
                      sx={{
                        fontSize: '12px',
                        lineHeight: '19px',
                        color: '#6B6760',
                      }}
                    >
                      {index > 0 ? `${'\u00B7'} ${item}` : item}
                    </Typography>
                  );
                })
              }
            </Stack>
          }
          {
            showEmailMessage &&
            <EmailMessage
              parsedEmail={parsedEmail}
            />
          }
          {
            !showEmailMessage &&
            <Message
              userRn={userRn}
              message={message.body.blob}
              emojis={message.emoji}
              editMode={editMode}
              onClickSave={onClickEditSave}
              onClickCancel={onClickEditCancel}
              onEmojiSelected={onEmojiSelected}
              emojisWithRequesterData={message.emojisWithRequesterData}
              truncate
            />
          }
        </Stack>
      </Stack>
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        copiedText={message.body.blob}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
        onEmojiSelected={onEmojiSelected}
        hideTool={{
          emoji: isMessageTypeEmail,
          copy: showEmailMessage,
          edit: isMessageTypeEmail || userRn !== message.submittedBy.resourceRn,
          delete: userRn !== message.submittedBy.resourceRn,
        }}
      />
    </Box>
  );
};
MessageFeedBox.propTypes = {
  message: PropTypes.object.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  userRn: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(MessageFeedBox));
